import { createContext, useContext, useEffect, useState } from "react";
import Keycloak from "keycloak-js";

const AuthContext = createContext(null);

const keycloak = new Keycloak({
    url: process.env.REACT_APP_KEYCLOAK_URL,
    realm: process.env.REACT_APP_KEYCLOAK_REALM,
    clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID,
});

export function AuthProvider({ children }) {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(null);

    useEffect(() => {
        let intervalId;

        keycloak
            .init({
                onLoad: 'check-sso',
                silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
                pkceMethod: 'S256',
            })
            .then((authenticated) => {
                setIsAuthenticated(authenticated);
                setIsLoading(false);

                if (authenticated) {
                    setToken(keycloak.token);
                    setUser({
                        email: keycloak.tokenParsed?.email,
                        username: keycloak.tokenParsed?.preferred_username,
                        id: keycloak.tokenParsed?.sub,
                    });
                }

                intervalId = setInterval(() => {
                    keycloak
                        .updateToken(70)
                        .then((refreshed) => {
                            if (refreshed) {
                                setToken(keycloak.token);
                            }
                        })
                        .catch(() => {});
                }, 60000);
            })
            .catch(() => {
                setIsLoading(false);
            });

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, []);

    const login = () => {
        keycloak.login();
    };

    const logout = () => {
        keycloak.logout({
            redirectUri: window.location.origin,
        });
    };

    const register = () => {
        keycloak.register({
            redirectUri: window.location.origin,
        });
    };

    const getToken = () => {
        return keycloak.token;
    };

    const value = {
        isAuthenticated,
        isLoading,
        user,
        token,
        login,
        logout,
        register,
        getToken,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
}
